import React from 'react';
import { useAuth } from '../auth/use-auth';

/** User display preferences for timezone and Monitor table columns. */
export interface DisplayPreferences {
  /** The IANA timezone string for formatting dates. */
  timezone: string;
  /** The column keys currently visible on the Monitor Recent table. */
  monitorColumns: string[];
  /** Number of rows per page on the Flows page. */
  flowsRowsPerPage: number;
  /** Number of rows per page on the Monitor Recent table. */
  monitorRowsPerPage: number;
}

/** Available rows-per-page options. */
export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

/** All available Monitor columns with metadata. */
export const MONITOR_COLUMNS = [
  { key: 'flow', label: 'Flow', required: true },
  { key: 'project', label: 'Project', required: false },
  { key: 'id', label: 'ID', required: false },
  { key: 'jobs', label: 'Jobs', required: false },
  { key: 'started', label: 'Started', required: true },
  { key: 'finished', label: 'Finished', required: true },
  { key: 'by', label: 'By', required: false },
  { key: 'status', label: 'Status', required: true },
  { key: 'runTime', label: 'Run Duration', required: false },
] as const;

/** Available timezone options for the display preferences dropdown. */
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

const DEFAULT_COLUMNS = ['flow', 'project', 'started', 'finished', 'by', 'status'];

const getStorageKey = (userId: string) => `jetstream-display-${userId}`;

const loadPreferences = (userId: string): DisplayPreferences => {
  try {
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        timezone: parsed.timezone ?? 'UTC',
        monitorColumns: parsed.monitorColumns ?? DEFAULT_COLUMNS,
        flowsRowsPerPage: parsed.flowsRowsPerPage ?? 20,
        monitorRowsPerPage: parsed.monitorRowsPerPage ?? 20,
      };
    }
  } catch {}
  return { timezone: 'UTC', monitorColumns: DEFAULT_COLUMNS, flowsRowsPerPage: 20, monitorRowsPerPage: 20 };
};

/**
 * A hook that provides user display preferences (timezone and Monitor column visibility).
 * Preferences are stored in localStorage per user.
 */
export const useDisplayPreferences = () => {
  const auth = useAuth();
  const userId = auth.user?.profile.sub ?? '';
  const [prefs, setPrefs] = React.useState<DisplayPreferences>(() => loadPreferences(userId));

  // Reload when user changes
  React.useEffect(() => {
    if (userId) {
      setPrefs(loadPreferences(userId));
    }
  }, [userId]);

  const savePreferences = React.useCallback((updated: DisplayPreferences) => {
    // Ensure required columns are always included
    const required = MONITOR_COLUMNS.filter(c => c.required).map(c => c.key);
    const cols = Array.from(new Set([...required, ...updated.monitorColumns]));
    const toSave = { ...updated, monitorColumns: cols };
    setPrefs(toSave);
    if (userId) {
      window.localStorage.setItem(getStorageKey(userId), JSON.stringify(toSave));
    }
  }, [userId]);

  return { prefs, savePreferences };
};
